import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Target,
  Shield,
  Users,
  Heart,
  CheckCircle,
  Globe,
  DollarSign,
  Award,
  ArrowRight
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const AboutUs = () => {
  const navigate = useNavigate();
  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center justify-center w-24 h-24 rounded-3xl mb-8"
            style={{ backgroundColor: brandColorLight }}
          >
            <ShoppingCart className="w-12 h-12" style={{ color: brandColor }} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About <span style={{ color: brandColor }}>EcoMart</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            EcoMart is Nigeria’s trusted online marketplace operated by SalsTech IT Solutions (RC 7398490), 
            connecting buyers and sellers in a safe and secure environment. 
            With our escrow payment system, buyers get guaranteed value for their purchases, 
            while sellers reach serious buyers and sell faster.
          </p>
          <button
            onClick={() => navigate('/categories')}
            className="px-8 py-3 rounded-xl font-semibold text-lg transition-all hover:shadow-lg inline-flex items-center"
            style={{
              backgroundColor: brandColor,
              color: 'white'
            }}
          >
            Start Shopping
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>

        {/* Mission Section */}
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-3xl p-8 md:p-12 mb-16 text-center">
          <Target className="w-12 h-12 mx-auto mb-6" style={{ color: brandColor }} />
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
            To create a safe, reliable, and efficient online marketplace in Nigeria where 
            buyers and sellers can transact with confidence. Our mission is to empower sellers to reach serious buyers 
            quickly while ensuring buyers get authentic products and seamless service.
          </p>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Why Choose EcoMart?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4">
              <Shield className="w-12 h-12 text-white p-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Secure Escrow Payments</h3>
                <p className="text-gray-600">Protects both buyers and sellers, ensuring safe transactions and peace of mind.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4">
              <DollarSign className="w-12 h-12 text-white p-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Selling</h3>
                <p className="text-gray-600">Our platform connects sellers with serious buyers to ensure products sell quickly.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4">
              <CheckCircle className="w-12 h-12 text-white p-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Verified Sellers</h3>
                <p className="text-gray-600">We verify sellers to maintain a trustworthy community and minimize fraud.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4">
              <Users className="w-12 h-12 text-white p-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Trusted Community</h3>
                <p className="text-gray-600">We build a safe marketplace by fostering respect, accountability, and reliable interactions.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4">
              <Globe className="w-12 h-12 text-white p-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Nationwide Reach</h3>
                <p className="text-gray-600">Buyers and sellers from all across Nigeria can connect and trade easily on EcoMart.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4">
              <Award className="w-12 h-12 text-white p-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Value for All</h3>
                <p className="text-gray-600">We ensure buyers get real value while sellers achieve meaningful sales efficiently.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl p-8 md:p-12 text-white text-center mb-16">
          <Heart className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Join the EcoMart Community</h2>
          <p className="text-lg mb-8 opacity-95 max-w-2xl mx-auto">
            Sign up today to start buying and selling safely with millions of Nigerians already enjoying EcoMart.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3 bg-white text-orange-600 font-semibold rounded-xl hover:bg-orange-50 transition-colors"
            >
              Create Free Account
            </button>
            <button
              onClick={() => navigate('/how-it-works')}
              className="px-8 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
            >
              Learn How It Works
            </button>
          </div>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default AboutUs;
